import { Outlet, Navigate, useLocation, useNavigate } from 'react-router';
import { useEffect, useRef } from 'react';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { AppSidebar } from '~/components/AppSidebar';
import { SidebarInset, useSidebar } from '~/components/ui/Sidebar';
import { Toaster } from '~/components/ui/Toaster';
import { useAuthStore } from '~/stores/useAuthStore';
import { useActiveTeam, useTeamStore } from '~/stores/useTeamStore';
import GlobalSpinner from '~/components/GlobalSpinner';
import { PrivateOutletBoundary } from '~/components/PrivateOutletBoundary';

function PrivateLayoutInner() {
  const user = useAuthStore((state) => state.user);
  const loading = useAuthStore((state) => state.loading);
  const location = useLocation();
  const { setOpen, isMobile, setOpenMobile } = useSidebar();
  const previousPathRef = useRef(location.pathname);
  const activeTeamId = useAuthStore((state) => state.activeTeamId);
  const loadTeamData = useTeamStore((state) => state.loadTeamData);
  const navigate = useNavigate();
  const activeTeam = useActiveTeam();
  const { reset: resetQueryErrors } = useQueryErrorResetBoundary();

  useEffect(() => {
    loadTeamData();
  }, [activeTeamId, loadTeamData]);

  const isChat = location.pathname.startsWith('/chat');
  const wasChat = previousPathRef.current.startsWith('/chat');

  useEffect(() => {
    if (isMobile && location.pathname !== previousPathRef.current) {
      setOpenMobile(false);
    }

    if (isChat && !wasChat) {
      setOpen(false);
    } else if (!isChat && wasChat) {
      setOpen(true);
    }

    previousPathRef.current = location.pathname;
  }, [isChat, wasChat, setOpen, location.pathname, isMobile, setOpenMobile]);

  useEffect(() => {
    if (!activeTeam?.brandProfile) return;

    if (location.pathname.startsWith('/dashboard/performance-tracking')) {
      if (!activeTeam.brandProfile.hasPerformanceTracking) {
        navigate('/dashboard');
      }
    }
    if (location.pathname.startsWith('/dashboard/leads-tracking')) {
      if (!activeTeam.brandProfile.hasLeadsTracking) {
        navigate('/dashboard');
      }
    }
    if (location.pathname.startsWith('/dashboard/channel-performance')) {
      if (!activeTeam.brandProfile.hasChannelPerformance) {
        navigate('/dashboard');
      }
    }
    if (location.pathname.startsWith('/dashboard/blinkit-brands')) {
      if (!activeTeam.brandProfile.hasBlinkitBrands) {
        navigate('/dashboard');
      }
    }
    if (location.pathname.startsWith('/dashboard/store-performance')) {
      if (!activeTeam.brandProfile.hasStorePerformance) {
        navigate('/dashboard');
      }
    }
  }, [activeTeam, location.pathname, navigate]);

  if (loading) {
    return <GlobalSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <>
      <AppSidebar />
      <SidebarInset>
        <PrivateOutletBoundary
          key={location.pathname}
          onReset={resetQueryErrors}
        >
          <Outlet />
        </PrivateOutletBoundary>
        <Toaster />
      </SidebarInset>
    </>
  );
}

export default function PrivateLayout() {
  return <PrivateLayoutInner />;
}
